import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { toLocalInput, fromLocalInput } from "@/lib/calendar";
import { FORMATI, STATI_POST } from "@/lib/ped";
import { ChannelPicker } from "./PedBits";
import { PostAiPanel } from "./PostAiPanel";
import { PostMedia } from "./PostMedia";
import { CommentsThread } from "@/components/core/CommentsThread";
import { ReservedLine } from "@/components/client/ReservedLine";
import { AttachmentsPanel } from "@/components/core/AttachmentsPanel";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Clapperboard } from "lucide-react";
import { PostMetrics } from "@/components/adminai/PostMetrics";
import { nextBestSlot } from "@/components/adminai/BestTimes";
import { Switch } from "@/components/ui/switch";
import { ClientFeedback } from "./ClientFeedback";

const L = ({ children }) => <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

const blank = (defaults) => {
  const d = defaults?.date ? new Date(defaults.date) : new Date(Date.now() + 86400e3);
  if (!defaults?.date || defaults.allDay) d.setHours(12, 0, 0, 0);
  return { titolo: "", data_pubblicazione: toLocalInput(d), canali: ["instagram"], formato: "post", testo: "", hashtag: "", note: "", socio_id: "none", stato: "bozza" };
};

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-xl" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

export const PostEditorDialog = ({ open, onOpenChange, post, defaults, clientId, users = [], onSaved }) => {
  const attRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [useBest, setUseBest] = useState(false);
  const applyBest = async (v) => {
    setUseBest(v);
    if (!v) return;
    try {
      const r = await api.get(`/admin-ai/clients/${clientId}/orari`, { params: { canale: f.canali.length === 1 ? f.canali[0] : "tutti" } });
      const d = nextBestSlot(r.data.top, f.data_pubblicazione);
      if (d) { setF((p) => ({ ...p, data_pubblicazione: toLocalInput(d) })); toast.success(`Orario consigliato${r.data.fonte === "stima" ? " (stima di settore)" : ""}: ${d.toLocaleString("it-IT", { weekday: "long", hour: "2-digit", minute: "2-digit" })}`); }
      else toast.error("Nessun orario consigliato disponibile");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [current, setCurrent] = useState(null);
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrent(post || null);
    setF(post ? { ...post, data_pubblicazione: toLocalInput(post.data_pubblicazione), socio_id: post.socio_id || "none" } : blank(defaults));
  }, [open, post, defaults]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.titolo.trim()) return toast.error("Inserisci il titolo / tema del post");
    if (!f.canali.length) return toast.error("Seleziona almeno un canale");
    setSaving(true);
    const body = { client_id: clientId, titolo: f.titolo, data_pubblicazione: fromLocalInput(f.data_pubblicazione), canali: f.canali, formato: f.formato,
      testo: f.testo, hashtag: f.hashtag, note: f.note, stato: f.stato, socio_id: f.socio_id === "none" ? null : f.socio_id };
    try {
      const r = current ? await api.put(`/posts/${current.id}`, body) : await api.post("/posts", body);
      await attRef.current?.flush(r.data.id);
      onSaved?.(r.data);
      if (current) { toast.success("Post aggiornato"); onOpenChange(false); }
      else { toast.success("Post creato: ora puoi allegare immagini e video"); setCurrent(r.data); }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const socioOpts = [{ value: "none", label: "Nessuno" }, ...users.filter((u) => u.is_active || u.id === f.socio_id).map((u) => ({ value: u.id, label: `${u.nome} ${u.cognome}` }))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto rounded-2xl core-scroll" data-testid="post-editor-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{current ? "Modifica post" : "Nuovo post"}</DialogTitle>
          <ReservedLine clientId={clientId} testid="post-editor-reserved" /></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-[1.25fr_1fr] gap-6">
          <div className="space-y-4">
            <div className="space-y-1.5"><L>Titolo / tema *</L><Input value={f.titolo} onChange={set("titolo")} className="rounded-xl" data-testid="post-form-titolo" placeholder="Es. Lancio del menù autunnale" /></div>
            <div className="space-y-1.5"><L>Canali *</L><ChannelPicker value={f.canali} onChange={set("canali")} testidPrefix="post-form-canale" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><L>Data e ora pubblicazione</L><Input type="datetime-local" value={f.data_pubblicazione} onChange={set("data_pubblicazione")} className="rounded-xl" data-testid="post-form-data" />
                {isAdmin && !current && <label className="flex items-center gap-2 text-xs font-semibold text-slate-700"><Switch checked={useBest} onCheckedChange={applyBest} data-testid="post-form-best-time" />Usa orario consigliato</label>}</div>
              <div className="space-y-1.5"><L>Formato</L><Pick value={f.formato} onChange={set("formato")} options={FORMATI} testid="post-form-formato" /></div>
              <div className="space-y-1.5"><L>Stato</L><Pick value={f.stato} onChange={set("stato")} options={STATI_POST} testid="post-form-stato" /></div>
              <div className="space-y-1.5"><L>Socio responsabile</L><Pick value={f.socio_id} onChange={set("socio_id")} options={socioOpts} testid="post-form-socio" /></div>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between"><L>Testo / caption</L><span className="text-[11px] text-slate-400" data-testid="post-form-char-count">{f.testo.length} caratteri</span></div>
              <Textarea rows={8} value={f.testo} onChange={set("testo")} className="rounded-xl" data-testid="post-form-testo" />
            </div>
            <div className="space-y-1.5"><L>Hashtag</L><Input value={f.hashtag} onChange={set("hashtag")} className="rounded-xl" placeholder="#esempio #brand" data-testid="post-form-hashtag" /></div>
            <div className="space-y-1.5"><L>Note interne</L><Textarea rows={2} value={f.note} onChange={set("note")} className="rounded-xl" data-testid="post-form-note" /></div>
          </div>
          <div className="space-y-5">
            <PostAiPanel clientId={clientId} tema={f.titolo} canali={f.canali} formato={f.formato} testo={f.testo}
              onApply={(v) => setF((p) => ({ ...p, titolo: p.titolo.trim() ? p.titolo : (v.titolo || ""), testo: v.caption, hashtag: v.hashtag }))} />
            {current ? <PostMedia post={current} onChange={(p) => { setCurrent(p); onSaved?.(p); }} />
              : <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid="post-media-disabled">Salva il post per allegare immagini e video.</div>}
            <AttachmentsPanel ref={attRef} tipo="post" targetId={current?.id} title="Allegati (documenti interni, non visibili nel portale)" testid="post-attachments" />
            {isAdmin && current?.script_id && (
              <Button type="button" variant="outline" onClick={() => { onOpenChange(false); nav(`/clienti/${clientId}?tab=script&script=${current.script_id}`); }}
                className="w-full rounded-full font-bold border-emerald-600 text-emerald-800" data-testid="post-linked-script"><Clapperboard className="h-4 w-4 mr-1.5" />Script collegato</Button>
            )}
            {isAdmin && current?.stato === "pubblicato" && <PostMetrics key={current.id} post={current} onChange={(p) => setCurrent(p)} />}
            {isAdmin && current?.origine?.analisi_id && (
              <Button type="button" variant="outline" onClick={() => { onOpenChange(false); nav(`/clienti/${clientId}?tab=script&analisi=${current.origine.analisi_id}&idea=${current.origine.idea}`); }}
                className="w-full rounded-full font-bold" data-testid="post-create-script"><Clapperboard className="h-4 w-4 mr-1.5" />Crea script video da questa idea</Button>
            )}
            {current && <ClientFeedback post={current} />}
            {current && <CommentsThread tipo="post" targetId={current.id} onChange={() => onSaved?.(current)} />}
          </div>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="post-form-cancel">{current && !post ? "Chiudi" : "Annulla"}</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6 font-bold" data-testid="post-form-submit">{saving ? "Salvataggio..." : current ? "Salva modifiche" : "Crea post"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
